import { useEffect, useState } from "react";
import { Plug, Key, CheckCircle2, Zap, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function FulfillmentTest() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fulfillment/test-order", {});
      setResult(data);
      toast.success("Test routed to supplier — no charge applied");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="fulfillment-test-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#FF6A1A] text-white w-9 h-9 rounded-lg flex items-center justify-center"><FlaskConical size={16} /></div>
          <div><p className="font-semibold">Order Fulfillment Test</p>
            <p className="text-xs text-zinc-400">Runs the full routing pipeline (stock + price + profit → supplier payload) in simulation. Never places a live order or charges your wallet.</p></div>
        </div>
        <Button size="sm" onClick={run} disabled={busy} data-testid="run-fulfillment-test-btn">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FlaskConical size={14} className="mr-1.5" />}Run Test
        </Button>
      </div>
      {result && (
        <div className="mt-2 text-xs space-y-2" data-testid="fulfillment-test-result">
          <div className={`rounded-lg p-3 ${result.verification.ok ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
            <p className="font-semibold">{result.verification.ok ? "All checks passed ✓" : "Warnings:"}</p>
            {result.verification.warnings.map((w, i) => <p key={i}>• {w}</p>)}
            <p className="text-zinc-500 mt-1">Product: {result.product.title}</p>
          </div>
          {result.routing.map((r, i) => (
            <div key={i} className="border border-zinc-100 rounded-lg p-3">
              <p className="font-medium capitalize">{r.supplier} <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">SIMULATED · NOT CHARGED</span></p>
              <p className="font-mono text-zinc-500 mt-1">Sim order: {r.simulated_response.supplier_order_id} · cost ${r.supplier_cost}</p>
              <pre className="bg-zinc-50 rounded p-2 mt-2 overflow-auto text-[11px] text-zinc-600">{JSON.stringify(r.payload, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Integrations() {
  const [suppliers, setSuppliers] = useState([]);
  const [creds, setCreds] = useState({});
  const [testing, setTesting] = useState("");

  const load = () => api.get("/admin/suppliers").then((r) => setSuppliers(r.data));
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const c = creds[key] || {};
    try {
      await api.put(`/admin/suppliers/${key}/credentials`, { credentials: c });
      toast.success("Credentials saved securely (encrypted)");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const test = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post(`/admin/suppliers/${key}/test`);
      if (data.ok) toast.success(`Connection OK · mode: ${data.mode} · ${data.categories} categories`);
      else toast.error(data.error || "Test failed");
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">API Integrations</h1><p className="text-sm text-zinc-500">Connect supplier APIs. Credentials are encrypted and never exposed to the storefront.</p></div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Zap size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">Until live credentials are added, each supplier runs on a realistic <b>mock catalog</b> so you can test the full import → sell → fulfill flow. Add real keys below to switch to the live API automatically.</p>
      </div>

      {suppliers.map((s) => (
        <div key={s.key} className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`integration-${s.key}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-zinc-900 text-white w-9 h-9 rounded-lg flex items-center justify-center"><Plug size={16} /></div>
              <div><p className="font-semibold">{s.name}</p>
                {s.connected ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 size={12} />Live API connected</span>
                  : <span className="text-xs text-zinc-400">Running on mock catalog</span>}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs flex items-center gap-1"><Key size={11} />API Email / Account</Label>
              <Input placeholder="supplier account email" onChange={(e) => setCreds({ ...creds, [s.key]: { ...creds[s.key], email: e.target.value } })} data-testid={`cred-email-${s.key}`} /></div>
            <div><Label className="text-xs flex items-center gap-1"><Key size={11} />API Key / Password</Label>
              <Input type="password" placeholder="••••••••" onChange={(e) => setCreds({ ...creds, [s.key]: { ...creds[s.key], api_key: e.target.value } })} data-testid={`cred-key-${s.key}`} /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={() => save(s.key)} data-testid={`save-cred-${s.key}`}>Save Credentials</Button>
            <Button size="sm" variant="outline" onClick={() => test(s.key)} disabled={testing === s.key} data-testid={`test-cred-${s.key}`}>
              {testing === s.key ? "Testing..." : "Test Connection"}
            </Button>
          </div>
          {s.key === "cjdropshipping" && (
            <p className="text-xs text-zinc-400 mt-3">Get CJ API credentials from your CJdropshipping account → Authorization → API. Enter the API email and API key/password above.</p>
          )}
        </div>
      ))}

      <FulfillmentTest />
    </div>
  );
}
